import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { useSelector } from "react-redux";

import { RootState } from "../../../../../redux/store";
import { DEFAULT_MAIN_COLOR } from "@constants/index";

import { Divers } from "./Divers";
import { Tables } from "./Tables";
import { Commandes } from "./Commandes";
import { Notifications } from "./Notifications";
import { ToggleButton, NavLinkContent, TooltipWrapper } from "./styled";

import "./index.css";

type TabsMenuType = {
    title: string;
    content: React.ReactNode;
    customClassName: string;
    icon: React.ReactNode;
};

// ─── Tab icons (module-level — no closure, safe to hoist) ─────
const LanguagesIcon = (): JSX.Element => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0 0 14.07 6H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" />
    </svg>
);
const HomeIcon = (): JSX.Element => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
    </svg>
);
const CommandesIcon = (): JSX.Element => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 3H14.82C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
    </svg>
);
const NotificationsIcon = (): JSX.Element => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
    </svg>
);

export function ModalContentWrapper(): JSX.Element {
    const { t } = useTranslation();
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        DEFAULT_MAIN_COLOR;

    const [activeTab, setActiveTab] = React.useState<number>(0);
    const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);

    const tabs: TabsMenuType[] = [
        {
            title: t("Languages"),
            content: <Divers />,
            customClassName: "servio_sid_languages",
            icon: <LanguagesIcon />,
        },
        {
            title: t("Home page"),
            content: <Tables />,
            customClassName: "servio_sid_homepage",
            icon: <HomeIcon />,
        },
        {
            title: t("Orders"),
            content: <Commandes />,
            customClassName: "servio_sid_commandes",
            icon: <CommandesIcon />,
        },
        {
            title: t("Notifications"),
            content: <Notifications />,
            customClassName: "servio_sid_notifications",
            icon: <NotificationsIcon />,
        },
    ];

    function toggleNav(tab: number): void {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    }

    return (
        <div
            className="d-grid__clz h-100"
            data-testid="servio-modal-container"
            style={{
                gridTemplateColumns: isCollapsed ? "60px 1fr" : "220px 1fr",
                transition: "grid-template-columns 0.3s ease",
            }}
        >
            <Nav
                tabs
                className="d-flex flex-column servio-sidebar__clz"
                style={{ backgroundColor: mainColor }}
                data-testid="servio-modal-sidebar-nav"
            >
                <ToggleButton
                    data-testid="servio-modal-sidebar-toggle-btn"
                    mainColor={mainColor}
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    title={isCollapsed ? t("Expand menu") : t("Collapse menu")}
                    aria-label={
                        isCollapsed ? t("Expand menu") : t("Collapse menu")
                    }
                    aria-expanded={!isCollapsed}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        style={{
                            transform: isCollapsed
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                        }}
                    >
                        <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                    </svg>
                </ToggleButton>

                {tabs.map((element, index) => (
                    <TooltipWrapper key={index}>
                        <NavItem
                            data-testid={`servio-modal-tab-item-${index}`}
                            className={classNames(
                                "pb-1 servio-menu-item__clz",
                                {
                                    "active servio-menu-item-active__clz":
                                        activeTab === index,
                                },
                                element.customClassName
                            )}
                            title={isCollapsed ? element.title : ""}
                        >
                            <NavLink
                                data-testid={`servio-modal-tab-link-${index}`}
                                className={classNames(
                                    "border-0 servio-nav-link__clz",
                                    {
                                        "active servio-nav-link-active__clz":
                                            activeTab === index,
                                    }
                                )}
                                onClick={() => toggleNav(index)}
                                style={{
                                    padding: isCollapsed ? "12px 0" : "12px",
                                }}
                            >
                                <NavLinkContent isCollapsed={isCollapsed}>
                                    {element.icon}
                                    <span>{element.title}</span>
                                </NavLinkContent>
                            </NavLink>
                        </NavItem>
                    </TooltipWrapper>
                ))}
            </Nav>

            <TabContent
                activeTab={activeTab}
                data-testid="servio-modal-tab-content"
                style={{ overflowY: "auto" }}
            >
                {tabs.map((element, index) => (
                    <TabPane
                        tabId={index}
                        key={index}
                        className="h-100"
                        data-testid={`servio-modal-tab-pane-${index}`}
                    >
                        {element.content}
                    </TabPane>
                ))}
            </TabContent>
        </div>
    );
}
